import { FC, useCallback, useMemo } from "react";
import { Box, Button, Dialog, Flex, Grid, Stack, Text } from "@chakra-ui/react";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";

import { CASE_FILE_KIND, CaseDocumentFileModel } from "@shared/models";
import { ErrorOutlineIcon, WarningIcon } from "@shared/components/Icons";
import { useLoader } from "@shared/hooks/LoaderHook";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";
import { CASE_DOCUMENT_LIMITS } from "@containers/Cases/constants";
import { UPLOAD_STATUS, UploadRow, useCaseDocumentUpload } from "@containers/Cases/hooks";
import { parseCaseDocument } from "@containers/Cases/store/actions";
import { pluralize } from "@containers/Cases/utils/caseDocuments";

import {
  CaseDocumentDropZone,
  CaseDocumentFileRow,
  CaseDocumentModalHeader,
  CaseDocumentRemoveFileButton,
  CaseDocumentUploadRow,
  SslSecuredNote,
} from "../CaseDocumentModal";
import { formatMegaBytes } from "../CaseDocuments.utils";
import MedicalRecordsLimitMeter from "./MedicalRecordsLimitMeter";

const LIMITS = CASE_DOCUMENT_LIMITS[CASE_FILE_KIND.MEDICAL];

const NO_FILES: CaseDocumentFileModel[] = [];

const BIG_FILE_PAGES = 6;

const getFooterNote = (rows: UploadRow[], limitError: string | null) => {
  const failedRows = rows.filter(({ status }) => status === UPLOAD_STATUS.FAILED).length;

  if (limitError) return "Over the limit. Remove files to continue.";
  if (!rows.length)
    return `Up to ${LIMITS.maxFiles} files, ${LIMITS.maxPages} pages and ${formatMegaBytes(LIMITS.maxTotalSize)} in total.`;
  if (failedRows) return `${pluralize(failedRows, "file")} won't be uploaded.`;
  if (rows.some(({ status }) => status !== UPLOAD_STATUS.READY)) return "Uploading…";
  return "All files ready.";
};

const getParseLabel = (newFiles: number, keptFiles: number, canParse: boolean) => {
  if (!canParse) return "Upload & parse";
  if (newFiles) return `Upload & parse ${pluralize(newFiles, "file")}`;

  return keptFiles ? `Parse ${pluralize(keptFiles, "file")}` : "Upload & parse";
};

const ReplaceNote: FC = () => (
  <Flex gap="10px" align="flex-start" px="14px" py={3} borderRadius="8px" bg="score.midBg" color="brand.warningText">
    <WarningIcon boxSize={4} mt="2px" flexShrink={0} color="score.midText" />
    <Text fontSize="13px" lineHeight={1.5}>
      Re-parsing replaces text that came from the medical summaries. Text from the PSR, and anything you’ve edited, is
      kept.
    </Text>
  </Flex>
);

const LimitNote: FC<{ title: string }> = ({ title }) => (
  <Flex
    gap="10px"
    align="flex-start"
    px="14px"
    py={3}
    borderRadius="8px"
    border="1px solid"
    borderColor="brand.error/30"
    bg="score.lowBg/50"
  >
    <ErrorOutlineIcon boxSize={4} mt="2px" flexShrink={0} color="score.lowText" />
    <Flex direction="column" gap="3px" fontSize="13px" lineHeight={1.5}>
      <Text fontWeight={FONT_WEIGHT.HEAVY} color="score.lowText">
        {title}
      </Text>
      <Text color="brand.textSecondary">
        Remove files to continue. These should be short medical summaries; full records aren’t needed.
      </Text>
    </Flex>
  </Flex>
);

const PagesTag: FC<{ pages: number | null }> = ({ pages }) =>
  pages !== null && pages >= BIG_FILE_PAGES ? (
    <Text flexShrink={0} fontSize="11px" fontWeight={FONT_WEIGHT.BOLD} color="score.lowText" whiteSpace="nowrap">
      {pages} PAGES
    </Text>
  ) : null;

const EmptyDropZone: FC = () => (
  <CaseDocumentDropZone
    title="Drag & drop files"
    formatNames={LIMITS.formatNames}
    note={
      <>
        <Text as="span" fontWeight={FONT_WEIGHT.HEAVY}>
          Please Note:
        </Text>{" "}
        Upload prepared medical summaries only. Full medical records aren’t needed.
      </>
    }
    limits={`Up to ${LIMITS.maxFiles} files, ${LIMITS.maxPages} pages and ${formatMegaBytes(LIMITS.maxTotalSize)} in total · ${formatMegaBytes(LIMITS.maxFileSize)} per file`}
  />
);

const CompactDropZone: FC = () => (
  <Flex
    align="center"
    justify="space-between"
    wrap="wrap"
    gap={3}
    px="18px"
    py="14px"
    borderRadius="10px"
    border="1.5px dashed"
    borderColor="gray.200"
    bg="brand.bg"
    cursor="pointer"
  >
    <Text fontSize="14px" color="brand.textSecondary">
      Drag more files here, or{" "}
      <Text as="span" color="brand.primary" fontWeight={FONT_WEIGHT.HEAVY}>
        browse
      </Text>
    </Text>
    <SslSecuredNote />
  </Flex>
);

interface MedicalRecordsUploadStepProps {
  caseId: number;
  documentFiles: CaseDocumentFileModel[];
  /** Lists the document's files so that they can be removed */
  replace: boolean;
  subtitle: string;
  onClose: () => void;
  onParse: () => void;
}

const MedicalRecordsUploadStep: FC<MedicalRecordsUploadStepProps> = ({
  caseId,
  documentFiles,
  replace,
  subtitle,
  onClose,
  onParse,
}) => {
  const dispatch = useDispatch();
  const {
    rows,
    totals,
    readyIds,
    existingFiles,
    replacedIds,
    limitError,
    canParse,
    addFiles,
    replaceRow,
    removeRow,
    removeExistingFile,
  } = useCaseDocumentUpload(caseId, CASE_FILE_KIND.MEDICAL, documentFiles, replace);

  const listedFiles = replace ? existingFiles : NO_FILES;
  const hasFiles = rows.length > 0 || listedFiles.length > 0;
  // Large files are pointed out only when they put the selection over the page limit.
  const isOverPages = totals.pages > LIMITS.maxPages;

  const { isLoading } = useLoader({
    name: "parseMedicalRecords",
    actionTypes: useMemo(() => [parseCaseDocument], []),
  });

  // The uploader calls back with a list for several files, despite its typing.
  const handleFiles = useCallback(
    (files: File | File[] | FileList) => addFiles(files instanceof File ? [files] : Array.from(files)),
    [addFiles],
  );

  const handleParse = () => {
    dispatch(
      parseCaseDocument.request({
        caseId,
        kind: CASE_FILE_KIND.MEDICAL,
        fileIds: readyIds,
        replacedFileIds: replacedIds,
        cb: () => onParse(),
      }),
    );
  };

  return (
    <>
      <CaseDocumentModalHeader
        title={replace ? "Replace Medical Summaries" : "Upload Medical Summaries"}
        subtitle={subtitle}
      />
      <Dialog.Body display="flex" flexDirection="column" gap={5}>
        {replace && <ReplaceNote />}
        <FileUploader classes="file-upload" name="files" multiple handleChange={handleFiles}>
          {hasFiles ? <CompactDropZone /> : <EmptyDropZone />}
        </FileUploader>

        {hasFiles && (
          <>
            <Grid templateColumns="repeat(auto-fit, minmax(180px, 1fr))" gap={4}>
              <MedicalRecordsLimitMeter label="Files" value={totals.files} limit={LIMITS.maxFiles} />
              <MedicalRecordsLimitMeter
                label="Pages"
                value={totals.pages}
                limit={LIMITS.maxPages}
                warnAt={LIMITS.maxPages - 1}
              />
              <MedicalRecordsLimitMeter
                label="Size"
                value={totals.size}
                limit={LIMITS.maxTotalSize}
                format={formatMegaBytes}
              />
            </Grid>
            {limitError && <LimitNote title={limitError} />}
            <Stack gap={2}>
              {listedFiles.map((file) => (
                <CaseDocumentFileRow key={file.id} file={file}>
                  {isOverPages && <PagesTag pages={file.pages} />}
                  <CaseDocumentRemoveFileButton fileName={file.name} onClick={() => removeExistingFile(file.id)} />
                </CaseDocumentFileRow>
              ))}
              {rows.map((row) => (
                <CaseDocumentUploadRow
                  key={row.key}
                  row={row}
                  tag={isOverPages && <PagesTag pages={row.pages} />}
                  onReplace={(file) => replaceRow(row, file)}
                  onRemove={() => removeRow(row)}
                />
              ))}
            </Stack>
          </>
        )}
      </Dialog.Body>
      <Dialog.Footer
        justifyContent="space-between"
        flexWrap="wrap"
        gap={3}
        mt={2}
        borderTop="1px solid"
        borderColor="gray.200"
      >
        <Box fontSize="13px" color="brand.emptyStateText">
          {getFooterNote(rows, limitError)}
        </Box>
        <Flex gap={3}>
          <Button variant="neutral" fontSize="14px" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" fontSize="14px" disabled={!canParse} loading={isLoading} onClick={handleParse}>
            {getParseLabel(readyIds.length, replacedIds.length ? existingFiles.length : 0, canParse)}
          </Button>
        </Flex>
      </Dialog.Footer>
    </>
  );
};

export default MedicalRecordsUploadStep;
